import React, { useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AppIcon from "../../../components/Core/AppIcon/AppIcon";
import AppText from "../../../components/Core/AppText/AppText";
import { EditableText } from "../../../components/Core/EditableText";
import Flex from "../../../components/Layout/Flex";
import TableLayout, { ActionsTableDataStyles } from "../../../components/Layout/TableLayout";
import ActionsPopOver, {
	PopOverAction,
} from "../../../components/Shared/ActionsPopover/ActionsPopover";
import RutRotSettingModal from "../../../components/Shared/RutRotSettingsModal/RutRotSettingsModal";
import { CompetenceItem } from "../../../models/Competence";
import { Price } from "../../../models/Contract";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { appTheme } from "../../../theme/Theme";
import { flatListServices } from "../../../utils/competence.utils";

interface ContractPricesProps {
	value?: Price[];
	fallbackPrices?: Price[];
	onChange?: (val: any) => any;
	errors?: any;
}

const ContractPrices = React.forwardRef<HTMLDivElement, ContractPricesProps>(
	({ value, onChange, errors, fallbackPrices = [] }, ref) => {
		const { data: organisation } = useOrganisation();
		const { t } = useTranslation();

		const [rutRotModal, setRutRotModal] = useState<{ price: Price; title?: string } | undefined>();
		const [prices, setPrices] = useState<Price[]>(value || []);

		const contractRowActions = (priceRow: Price, title?: string): PopOverAction[] => {
			return [
				{
					text: "RUT_ROT_SETTINGS",
					action: () => setRutRotModal({ price: priceRow, title }),
				},
				{
					text: "REMOVE",
					textStyles: { color: "danger" },
					action: () => deletePrice(priceRow.branch, priceRow.service),
				},
			];
		};

		const onUpdatePrices = (
			branchAndService: { branch: string; service: string },
			update: Partial<Price>
		) => {
			const updatedPrices = prices.map((p) => {
				if (p.branch === branchAndService.branch && p.service === branchAndService.service) {
					return { ...p, ...update };
				}
				return p;
			});

			setPrices(updatedPrices);
			onChange && onChange(updatedPrices);
		};

		const deletePrice = (branch: string, service: string) => {
			let newPrices = prices.filter((p) => !(p.branch === branch && p.service === service));

			setPrices(newPrices);
			onChange && onChange(newPrices);
		};

		useEffect(() => {
			setPrices(value || []);
		}, [value]);

		const priceList = useMemo(() => {
			if (organisation) {
				const list = flatListServices(organisation);

				return list.map((item) => {
					let pricesForService = prices.find(
						(p) => p.branch === item.branch.key && p.service === item.service.key
					);

					if (!pricesForService) {
						pricesForService = fallbackPrices.find(
							(p) => p.branch === item.branch.key && p.service === item.service.key
						);

						if (pricesForService) {
							pricesForService.inherited = true;
						}
					}

					return {
						...item,
						price: pricesForService,
					};
				});
			}
			return [];
		}, [organisation, prices, fallbackPrices]);

		function handleAddRow({
			branch,
			service,
		}: {
			branch: CompetenceItem;
			service: CompetenceItem;
		}) {
			const existingInheritedPrice = prices.find(
				(p) => p.branch === branch.key && p.service === service.key
			);

			if (existingInheritedPrice) {
				existingInheritedPrice.inherited = false;
			} else {
				prices.push({
					branch: branch.key,
					service: service.key,
					minHours: 4,
					vat: 0.25,
					price: 0,
				});
			}

			setPrices([...prices]);
			onChange && onChange(prices);
		}

		useEffect(() => {}, [value]);

		return (
			<Styles ref={ref}>
				<Flex margin="2rem 0 0 0" direction="column" gap="1rem" maxWidth="1450px">
					<TableLayout selectable={false}>
						<table>
							<thead>
								<tr>
									<th>{t("CONTRACT_SERVICE")}</th>
									<th style={{ minWidth: 150 }}>{t("CONTRACT_SERVICE_HOURLY_PRICE")}</th>
									<th style={{ width: 100 }}>{t("CONTRACT_SERVICE_VAT")}</th>
									<th style={{ width: 180 }}>{t("CONTRACT_SERVICE_ARTICLE_NUMBER")}</th>
									<th>{t("CONTRACT_SERVICE_TIME")}</th>
									<th style={{ width: 140 }}>{t("contract.hidden")}</th>
									<th style={{ minWidth: 120 }}></th>
								</tr>
							</thead>
							<tbody>
								{priceList.map(({ price, branch, service }) => (
									<tr
										key={branch.key + "_" + service.key}
										style={{ height: 50 }}
										className={price && !price.inherited ? "" : "not-in-contract"}
										onClick={() => (!price || price.inherited) && handleAddRow({ branch, service })}
									>
										<td
											style={{
												userSelect: "none",
												color: price?.inherited ? appTheme.colors.COLOR_GRAY_TEXT_LIGHT : undefined,
											}}
										>
											{branch.name.sv} - {service.name.sv}
										</td>
										<td>
											{price && (
												<EditableText
													type="number"
													value={price.price + ""}
													suffix=" SEK"
													inputWidth={"3rem"}
													onSave={(val) =>
														onUpdatePrices(
															{
																branch: branch.key,
																service: service.key,
															},
															{ price: Number(val) }
														)
													}
													validate={(val) => !isNaN(Number(val)) && Number(val) > 0}
													disableEditing={price.inherited}
												/>
											)}
										</td>
										<td>
											{price && (
												<EditableText
													type="number"
													value={((price.vat || 0) * 100).toFixed(0)}
													suffix="%"
													inputWidth={"3rem"}
													displayValueFormatter={(val) => val?.replace(".", ",")}
													onSave={(val) =>
														onUpdatePrices(
															{
																branch: branch.key,
																service: service.key,
															},
															{ vat: parseInt(val) / 100 }
														)
													}
													validate={(val) =>
														!isNaN(Number(val)) && Number(val) >= 0 && Number(val) < 50
													}
													disableEditing={price.inherited}
												/>
											)}
										</td>
										<td>
											{price && (
												<EditableText
													type="input"
													value={price?.articleNumber || ""}
													inputWidth={"8rem"}
													placeholder="Ange artikelnr"
													onSave={(val) =>
														onUpdatePrices(
															{
																branch: branch.key,
																service: service.key,
															},
															{ articleNumber: val }
														)
													}
													disableEditing={price.inherited}
												/>
											)}
										</td>
										<td>
											{price && (
												<EditableText
													type="number"
													value={(price?.minHours || "") + ""}
													suffix=" tim"
													inputWidth={"3rem"}
													displayValueFormatter={(val) => val?.replace(".", ",")}
													onSave={(val) =>
														onUpdatePrices(
															{
																branch: branch.key,
																service: service.key,
															},
															{ minHours: Number(val) }
														)
													}
													disableEditing={price.inherited}
												/>
											)}
										</td>
										<td style={{ userSelect: "none" }}>
											{price && (
												<Flex
													gap={"0.25rem"}
													cursor={price.inherited ? undefined : "pointer"}
													onClick={() =>
														!price.inherited &&
														onUpdatePrices(
															{
																branch: branch.key,
																service: service.key,
															},
															{ hidden: !price.hidden }
														)
													}
												>
													{price.hidden ? (
														<>
															<AppIcon icon="hidden" color={appTheme.colors.COLOR_DANGER} />
															<AppText size="s" color="danger">
																Dolt
															</AppText>
														</>
													) : (
														<>
															<AppIcon icon="visible" />
														</>
													)}
												</Flex>
											)}
										</td>
										{price && !price.inherited ? (
											<ActionsTableDataStyles>
												<Flex
													justifyContent="flex-end"
													alignItems="center"
													direction="row"
													minWidth="120px"
													gap={"1rem"}
												>
													{price.taxRutRot?.type && (
														<AppText
															weight="bold"
															size="s"
															onClick={() =>
																setRutRotModal({
																	price,
																	title: service.name.sv,
																})
															}
														>
															{price?.taxRutRot?.type} {price.taxRutRot.percentage}%
														</AppText>
													)}

													{<ActionsPopOver actions={contractRowActions(price, service.name.sv)} />}
												</Flex>
											</ActionsTableDataStyles>
										) : (
											<td></td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</TableLayout>

					{errors && (
						<AppText margin="0.5rem 0 0 0" size="s" color="danger">
							{errors && t(errors.message)}
						</AppText>
					)}
				</Flex>
				{rutRotModal && (
					<RutRotSettingModal
						priceRow={rutRotModal.price}
						title={rutRotModal.title || ""}
						onCancel={() => setRutRotModal(undefined)}
						onSubmit={(taxRutRot) => {
							onUpdatePrices(
								{
									branch: rutRotModal.price.branch,
									service: rutRotModal.price.service,
								},
								{ taxRutRot }
							);
							setRutRotModal(undefined);
						}}
					/>
				)}
			</Styles>
		);
	}
);

ContractPrices.displayName = "ContractPrices";

export default ContractPrices;

interface PricesControllerProps extends ContractPricesProps {
	control: Control<any, any>;
	name: string;
}

export const ContractPriceController: React.FC<PricesControllerProps> = ({
	control,
	name,
	...rest
}) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => <ContractPrices {...field} {...rest} />}
		/>
	);
};

const Styles = styled.div`
	.not-in-contract {
		cursor: pointer;

		td {
			color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT_LIGHT};
		}
	}
`;
